import type { SceneDef } from "../../genesis/schema";

/**
 * Le Parcours du Disciple — Les Deux Chemins (jeu 1, scène 1) — VERSION DENSE (vague 2).
 * Rendered entirely from data by the WorldManager (Genesis engine).
 *
 * Signature mechanics (Mt 7:13-14), densifiées :
 *  - Deux chemins qui divergent au carrefour : la large avenue clinquante (foule
 *    dense, fausses lumières de la foire, stèles sombres) vs la voie étroite
 *    bordée d'arbres qui monte vers la Porte Étroite lumineuse.
 *  - 5 fardeaux nommés (orgueil, peur, amertume, doute, mensonge) à ramasser
 *    (pickup) et à déposer dans les eaux de la repentance.
 *  - 6 fragments du Psaume 119:105 à rassembler sur la Lampe de la Parole.
 *  - La rivière du baptême à traverser en s'y tenant (lever_action, Ac 2:38),
 *    la Source d'eau vive au puits de la Samaritaine (Jn 4), la Cloche de la
 *    Mission près de la porte (Mt 28:19).
 *  - Le jardin du cœur : 6 semences (foi, amour, espérance, paix, joie,
 *    patience — Ga 5:22) à planter (drag_drop).
 *  - L'autel de prière : 5 pierres vivantes à assembler (1 Pi 2:5).
 *  - La table du partage : 5 provisions à apporter (Ac 2:44-46).
 *  - 3 brebis égarées à ramener à la bergerie (guide_target, Lc 15:4-7).
 *  - PNJ : Nicodème (Jn 3), André (Jn 1:41-42), la Samaritaine (Jn 4),
 *    Philippe l'évangéliste (Ac 8).
 *
 * Ancres bibliques : Mt 7:13-14 (les deux chemins), Jn 3:3-7 (nouvelle naissance),
 * Mt 13:22 (les épines qui étouffent), Ac 2:38-42 (repentance et baptême),
 * Ps 119:105 (la lampe de la Parole), 1 Pi 2:5 (pierres vivantes),
 * Lc 15:4-7 (la brebis perdue), Mt 28:19-20 (faites des disciples).
 * Cadrage d'espérance : la voie étroite mène à la VIE ; rien de sombre n'est
 * glorifié, tout pointe vers la lumière. Le Père n'est jamais figuré.
 */
export const parcoursDuDisciple: SceneDef = {
  id: "parcours-du-disciple/scene-1",
  game: "parcours-du-disciple",
  title: { fr: "Le Parcours du Disciple — Les Deux Chemins", en: "The Disciple's Journey — The Two Ways" },
  order: 1,
  keyName: { fr: "Clé de la Porte Étroite", en: "Key of the Narrow Gate" },
  verseBrand: "Matthieu 16:19",
  env: {
    groundSize: 60,
    skyColor: "#bcd6ea",
    groundColor: "#6f8f42",
    lighting: "day",
    props: [
      // La foule dense du large chemin — « il y en a beaucoup qui entrent par là » (Mt 7:13).
      { id: "foule_large", shape: "person", pos: [17, 0, -10], color: "#c8b56a", size: 1.05, scatter: 26, scatterRadius: 12 },
      // Stèles sombres : les zones de distraction qui jalonnent la large avenue.
      { id: "steles_sombres", shape: "stele", pos: [13, 0, -18], color: "#3a3a40", size: 1.5, scatter: 9, scatterRadius: 9 },
      // Fausses lumières dorées de la foire clinquante (séduction des richesses).
      { id: "lumieres_trompeuses", shape: "cone", pos: [19, 0, -16], color: "#e3b93a", size: 1.1, scatter: 14, scatterRadius: 10 },
      // Foule de la foire autour du puits et des étals de l'avenue est.
      { id: "foule_foire", shape: "person", pos: [22, 0, 2], color: "#b9a274", size: 1, scatter: 12, scatterRadius: 7 },
      // Lampions factices suspendus au-dessus des étals de la foire (y > 0).
      { id: "lampions_foire", shape: "flame", pos: [18, 2, -2], color: "#e8c46a", size: 0.35, scatter: 12, scatterRadius: 8 },
      // Bosquet paisible qui borde la voie étroite (le chemin resserré mais vivant).
      { id: "bosquet_etroit", shape: "tree", pos: [-20, 0, -14], color: "#3f6b3a", size: 1.8, scatter: 18, scatterRadius: 10 },
      // Forêt du sud-ouest qui encadre le départ du sentier près du spawn.
      { id: "foret_depart", shape: "tree", pos: [-12, 0, 18], color: "#46703c", size: 1.6, scatter: 16, scatterRadius: 8 },
      // Oliveraie à l'est de la table du partage (l'hospitalité du pays).
      { id: "oliveraie_est", shape: "tree", pos: [24, 0, 12], color: "#6d7d46", size: 1.4, scatter: 12, scatterRadius: 7 },
      // Buissons de la prairie du carrefour, autour du point de départ.
      { id: "buissons_prairie", shape: "cone", pos: [6, 0, 20], color: "#5c7d3f", size: 0.5, scatter: 18, scatterRadius: 9 },
      // Fleurs du jardin du cœur — la bonne terre qui porte du fruit (Mt 13:23).
      { id: "fleurs_jardin", shape: "sphere", pos: [-21, 0, 8], color: "#e58fb1", size: 0.28, scatter: 24, scatterRadius: 8 },
      // Rochers du sentier montant, autour de l'autel des pierres vivantes.
      { id: "rochers_sentier", shape: "box", pos: [-24, 0, -2], color: "#8a8578", size: 0.7, scatter: 10, scatterRadius: 9 },
      // Vagues de la rivière du baptême qui coupe la voie étroite.
      { id: "vagues_bapteme", shape: "water", pos: [-14, 0, -11], color: "#4c86bd", size: 1.2, scatter: 8, scatterRadius: 7 },
      // Collines d'horizon derrière la porte : le pays de la promesse.
      { id: "collines_horizon", shape: "mountain", pos: [-4, 0, -26], color: "#7c8a67", size: 3, scatter: 5, scatterRadius: 10 },
      // Flammes célestes suspendues au-dessus de la Porte Étroite lumineuse (y > 0).
      { id: "flammes_celestes", shape: "flame", pos: [-14, 3, -25], color: "#ffd27a", size: 0.6, scatter: 4, scatterRadius: 2 },
    ],
  },
  spawn: [0, 0, 16],
  difficulty: {
    // goalCount scale sur le VRAI total d'interactables avançants :
    // 11 pickup (5 fardeaux + 6 fragments) + 16 drag_drop (6 semences + 5 pierres
    // + 5 provisions) + 3 lever_action + 3 guide_target = 33.
    D: { goalCount: 11, hints: true },
    A: { goalCount: 22, hints: true },
    E: { goalCount: 33, hints: false, timerS: 480 },
  },
  interactables: [
    // ——— Les deux portes (Mt 7:13-14) ———
    {
      id: "narrow_gate", type: "build_slot",
      label: { fr: "La Porte Étroite lumineuse", en: "The luminous Narrow Gate" },
      pos: [-14, 0, -26], shape: "gate", color: "#f2d47a", size: 4, targetId: "narrow_gate",
    },
    // ——— Repentance : 5 fardeaux nommés à déposer dans l'eau ———
    {
      id: "eaux_repentance", type: "build_slot",
      label: { fr: "Les Eaux de la Repentance", en: "The Waters of Repentance" },
      pos: [2, 0, 2], shape: "water", color: "#3f79b5", size: 3, targetId: "eaux_repentance",
    },
    {
      id: "fardeau_orgueil", type: "pickup",
      label: { fr: "Fardeau de l'orgueil", en: "Burden of pride" },
      pos: [-5, 0, 11], shape: "box", color: "#8a4b4b", size: 0.9, targetId: "eaux_repentance", count: 1,
    },
    {
      id: "fardeau_peur", type: "pickup",
      label: { fr: "Fardeau de la peur", en: "Burden of fear" },
      pos: [5, 0, 12], shape: "box", color: "#5a5a6b", size: 0.85, targetId: "eaux_repentance", count: 1,
    },
    {
      id: "fardeau_amertume", type: "pickup",
      label: { fr: "Fardeau de l'amertume", en: "Burden of bitterness" },
      pos: [0, 0, 12], shape: "sphere", color: "#6b4a2b", size: 0.8, targetId: "eaux_repentance", count: 1,
    },
    {
      id: "fardeau_doute", type: "pickup",
      label: { fr: "Fardeau du doute", en: "Burden of doubt" },
      pos: [-8, 0, 15], shape: "box", color: "#4f5d6b", size: 0.85, targetId: "eaux_repentance", count: 1,
    },
    {
      id: "fardeau_mensonge", type: "pickup",
      label: { fr: "Fardeau du mensonge", en: "Burden of falsehood" },
      pos: [8, 0, 16], shape: "sphere", color: "#7a5b3a", size: 0.8, targetId: "eaux_repentance", count: 1,
    },
    // ——— La Lampe de la Parole : rassembler les 6 fragments du Psaume 119:105 ———
    {
      id: "lampe_parole", type: "build_slot",
      label: { fr: "La Lampe de la Parole (Psaume 119:105)", en: "The Lamp of the Word (Psalm 119:105)" },
      pos: [-20, 0, -21], shape: "flame", color: "#f4d06f", size: 1.6, targetId: "lampe_parole",
    },
    {
      id: "fragment_verset_1", type: "pickup",
      label: { fr: "Fragment 1 — « Ta parole »", en: "Fragment 1 — \"Your word\"" },
      pos: [-3, 0, 5], shape: "sphere", color: "#f0e3a8", size: 0.45, targetId: "lampe_parole", count: 1,
    },
    {
      id: "fragment_verset_2", type: "pickup",
      label: { fr: "Fragment 2 — « est une lampe »", en: "Fragment 2 — \"is a lamp\"" },
      pos: [-7, 0, 1], shape: "sphere", color: "#f0e3a8", size: 0.45, targetId: "lampe_parole", count: 1,
    },
    {
      id: "fragment_verset_3", type: "pickup",
      label: { fr: "Fragment 3 — « à mes pieds »", en: "Fragment 3 — \"to my feet\"" },
      pos: [-10, 0, -3], shape: "sphere", color: "#f0e3a8", size: 0.45, targetId: "lampe_parole", count: 1,
    },
    {
      id: "fragment_verset_4", type: "pickup",
      label: { fr: "Fragment 4 — « et une lumière »", en: "Fragment 4 — \"and a light\"" },
      pos: [-14, 0, -6], shape: "sphere", color: "#f0e3a8", size: 0.45, targetId: "lampe_parole", count: 1,
    },
    {
      id: "fragment_verset_5", type: "pickup",
      label: { fr: "Fragment 5 — « sur mon »", en: "Fragment 5 — \"for my\"" },
      pos: [-16, 0, -11], shape: "sphere", color: "#f0e3a8", size: 0.45, targetId: "lampe_parole", count: 1,
    },
    {
      id: "fragment_verset_6", type: "pickup",
      label: { fr: "Fragment 6 — « sentier »", en: "Fragment 6 — \"path\"" },
      pos: [-18, 0, -15], shape: "sphere", color: "#f0e3a8", size: 0.45, targetId: "lampe_parole", count: 1,
    },
    // ——— La rivière du baptême (Ac 2:38) — s'y tenir pour la traverser ———
    {
      id: "riviere_bapteme", type: "lever_action",
      label: { fr: "La Rivière du Baptême", en: "The River of Baptism" },
      pos: [-8, 0, -12], shape: "water", color: "#2f6fae", size: 2.6, holdS: 3,
    },
    // ——— La Source d'eau vive au puits (Jn 4:14) ———
    {
      id: "source_eau_vive", type: "lever_action",
      label: { fr: "La Source d'eau vive (Jean 4:14)", en: "The Spring of living water (John 4:14)" },
      pos: [20, 0, -6], shape: "water", color: "#5aa7c9", size: 1.8, holdS: 2.5,
    },
    // ——— La Cloche de la Mission (Mt 28:19) près de la porte ———
    {
      id: "cloche_mission", type: "lever_action",
      label: { fr: "La Cloche de la Mission (Matthieu 28:19)", en: "The Bell of the Mission (Matthew 28:19)" },
      pos: [-6, 0, -22], shape: "cone", color: "#c9a24b", size: 1.3, holdS: 2,
    },
    // ——— Le jardin du cœur : planter 6 semences (fruit de l'Esprit, Ga 5:22) ———
    {
      id: "jardin_coeur", type: "build_slot",
      label: { fr: "Le Jardin du Cœur", en: "The Garden of the Heart" },
      pos: [-22, 0, 4], shape: "tree", color: "#4c7a44", size: 2.2, targetId: "jardin_coeur",
    },
    {
      id: "semence_foi", type: "drag_drop",
      label: { fr: "Semence de la foi", en: "Seed of faith" },
      pos: [-16, 0, 8], shape: "sphere", color: "#e8d76a", size: 0.5, targetId: "jardin_coeur", count: 1,
    },
    {
      id: "semence_amour", type: "drag_drop",
      label: { fr: "Semence de l'amour", en: "Seed of love" },
      pos: [-25, 0, 9], shape: "sphere", color: "#d98a7a", size: 0.5, targetId: "jardin_coeur", count: 1,
    },
    {
      id: "semence_esperance", type: "drag_drop",
      label: { fr: "Semence de l'espérance", en: "Seed of hope" },
      pos: [-20, 0, 12], shape: "sphere", color: "#8ac3d9", size: 0.5, targetId: "jardin_coeur", count: 1,
    },
    {
      id: "semence_paix", type: "drag_drop",
      label: { fr: "Semence de la paix", en: "Seed of peace" },
      pos: [-27, 0, 1], shape: "sphere", color: "#a8cf9e", size: 0.5, targetId: "jardin_coeur", count: 1,
    },
    {
      id: "semence_joie", type: "drag_drop",
      label: { fr: "Semence de la joie", en: "Seed of joy" },
      pos: [-17, 0, 13], shape: "sphere", color: "#f0b45c", size: 0.5, targetId: "jardin_coeur", count: 1,
    },
    {
      id: "semence_patience", type: "drag_drop",
      label: { fr: "Semence de la patience", en: "Seed of patience" },
      pos: [-13, 0, 12], shape: "sphere", color: "#b39ddb", size: 0.5, targetId: "jardin_coeur", count: 1,
    },
    // ——— L'autel de prière : assembler 5 pierres vivantes (1 Pi 2:5) ———
    {
      id: "autel_pierres", type: "build_slot",
      label: { fr: "L'Autel de Prière en pierres vivantes", en: "The Prayer Altar of living stones" },
      pos: [-25, 0, -10], shape: "stele", color: "#a99d84", size: 2, targetId: "autel_pierres",
    },
    {
      id: "pierre_vivante_priere", type: "drag_drop",
      label: { fr: "Pierre vivante de la prière", en: "Living stone of prayer" },
      pos: [-22, 0, -5], shape: "box", color: "#9b9284", size: 0.6, targetId: "autel_pierres", count: 1,
    },
    {
      id: "pierre_vivante_louange", type: "drag_drop",
      label: { fr: "Pierre vivante de la louange", en: "Living stone of praise" },
      pos: [-27, 0, -6], shape: "box", color: "#a89b7f", size: 0.6, targetId: "autel_pierres", count: 1,
    },
    {
      id: "pierre_vivante_service", type: "drag_drop",
      label: { fr: "Pierre vivante du service", en: "Living stone of service" },
      pos: [-24, 0, -14], shape: "box", color: "#8f8878", size: 0.6, targetId: "autel_pierres", count: 1,
    },
    {
      id: "pierre_vivante_pardon", type: "drag_drop",
      label: { fr: "Pierre vivante du pardon", en: "Living stone of forgiveness" },
      pos: [-21, 0, -9], shape: "box", color: "#b0a68e", size: 0.6, targetId: "autel_pierres", count: 1,
    },
    {
      id: "pierre_vivante_perseverance", type: "drag_drop",
      label: { fr: "Pierre vivante de la persévérance", en: "Living stone of perseverance" },
      pos: [-28, 0, -12], shape: "box", color: "#948a76", size: 0.6, targetId: "autel_pierres", count: 1,
    },
    // ——— La table du partage : apporter 5 provisions (Ac 2:44-46) ———
    {
      id: "table_partage", type: "build_slot",
      label: { fr: "La Table du Partage (Actes 2:46)", en: "The Table of Sharing (Acts 2:46)" },
      pos: [13, 0, 8], shape: "box", color: "#a5713f", size: 2, targetId: "table_partage",
    },
    {
      id: "provision_pain", type: "drag_drop",
      label: { fr: "Pain à rompre ensemble", en: "Bread to break together" },
      pos: [10, 0, 13], shape: "box", color: "#d8b26a", size: 0.5, targetId: "table_partage", count: 1,
    },
    {
      id: "provision_poisson", type: "drag_drop",
      label: { fr: "Poissons du partage", en: "Fish to share" },
      pos: [16, 0, 12], shape: "sphere", color: "#7fa6b8", size: 0.5, targetId: "table_partage", count: 1,
    },
    {
      id: "provision_huile", type: "drag_drop",
      label: { fr: "Cruche d'huile", en: "Jar of oil" },
      pos: [18, 0, 5], shape: "cylinder", color: "#c9a24b", size: 0.55, targetId: "table_partage", count: 1,
    },
    {
      id: "provision_figues", type: "drag_drop",
      label: { fr: "Panier de figues", en: "Basket of figs" },
      pos: [13, 0, 15], shape: "sphere", color: "#8a6b4a", size: 0.5, targetId: "table_partage", count: 1,
    },
    {
      id: "provision_eau", type: "drag_drop",
      label: { fr: "Outre d'eau fraîche", en: "Skin of fresh water" },
      pos: [9, 0, 7], shape: "capsule", color: "#6f95b5", size: 0.55, targetId: "table_partage", count: 1,
    },
    // ——— Les brebis égarées à ramener à la bergerie (Lc 15:4-7) ———
    {
      id: "bergerie_bonne", type: "build_slot",
      label: { fr: "La Bergerie du Bon Berger (Jean 10:11)", en: "The Good Shepherd's fold (John 10:11)" },
      pos: [-26, 0, -18], shape: "gate", color: "#b08d57", size: 2.4, targetId: "bergerie_bonne",
    },
    {
      id: "brebis_egaree_foire", type: "guide_target",
      label: { fr: "Brebis égarée près de la foire", en: "Lost sheep near the fair" },
      pos: [25, 0, -2], shape: "animal", color: "#e8e2d4", size: 0.9, targetId: "bergerie_bonne", count: 1,
    },
    {
      id: "brebis_egaree_steles", type: "guide_target",
      label: { fr: "Brebis égarée parmi les stèles", en: "Lost sheep among the steles" },
      pos: [22, 0, -14], shape: "animal", color: "#ded6c4", size: 0.9, targetId: "bergerie_bonne", count: 1,
    },
    {
      id: "brebis_egaree_ravin", type: "guide_target",
      label: { fr: "Brebis égarée au bord du ravin", en: "Lost sheep at the edge of the ravine" },
      pos: [17, 0, -20], shape: "animal", color: "#e2dbc9", size: 0.9, targetId: "bergerie_bonne", count: 1,
    },
    // ——— PNJ nommés sur le chemin ———
    {
      id: "nicodeme", type: "npc_dialog",
      label: { fr: "Nicodème (Jean 3)", en: "Nicodemus (John 3)" },
      pos: [-4, 0, -6], shape: "person", color: "#7a6ba0", size: 1.1,
    },
    {
      id: "andre", type: "npc_dialog",
      label: { fr: "André, frère de Simon (Jean 1:41-42)", en: "Andrew, Simon's brother (John 1:41-42)" },
      pos: [4, 0, -14], shape: "person", color: "#6b8ba0", size: 1.1,
    },
    {
      id: "samaritaine", type: "npc_dialog",
      label: { fr: "La Samaritaine du puits (Jean 4)", en: "The Samaritan woman at the well (John 4)" },
      pos: [23, 0, -8], shape: "person", color: "#a08a6b", size: 1.1,
    },
    {
      id: "philippe", type: "npc_dialog",
      label: { fr: "Philippe l'évangéliste (Actes 8:35)", en: "Philip the evangelist (Acts 8:35)" },
      pos: [1, 0, -24], shape: "person", color: "#7a9a6b", size: 1.1,
    },
    // ——— Panneaux bibliques ———
    {
      id: "panel_mt7", type: "panel",
      label: { fr: "Le carrefour des deux chemins", en: "The crossroads of the two ways" },
      pos: [0, 0, 7], shape: "stele", color: "#b9a06a", size: 1.4, panelId: "p_mt7",
    },
    {
      id: "panel_jn3", type: "panel",
      label: { fr: "Naître de nouveau", en: "Born anew" },
      pos: [-11, 0, -8], shape: "stele", color: "#9fb0c4", size: 1.3, panelId: "p_jn3",
    },
    {
      id: "stele_richesses", type: "panel",
      label: { fr: "Stèle sombre : la séduction des richesses", en: "Dark stele: the deceitfulness of riches" },
      pos: [14, 0, -13], shape: "stele", color: "#44404a", size: 1.5, panelId: "p_mt13",
    },
    {
      id: "panel_ps119", type: "panel",
      label: { fr: "La lampe de la Parole", en: "The lamp of the Word" },
      pos: [-15, 0, -19], shape: "stele", color: "#d8c27a", size: 1.3, panelId: "p_ps119",
    },
    {
      id: "panel_ac2", type: "panel",
      label: { fr: "Repentance, baptême et communauté", en: "Repentance, baptism and community" },
      pos: [-4, 0, -1], shape: "stele", color: "#7fa0b5", size: 1.3, panelId: "p_ac2",
    },
    {
      id: "panel_mt28", type: "panel",
      label: { fr: "Faites des disciples", en: "Make disciples" },
      pos: [-9, 0, -25], shape: "stele", color: "#a0b57f", size: 1.3, panelId: "p_mt28",
    },
  ],
  objectives: [
    {
      id: "obj_fardeaux", kind: "collect",
      label: { fr: "Dépose les 5 fardeaux nommés dans les eaux de la repentance", en: "Lay the 5 named burdens into the waters of repentance" },
      target: "eaux_repentance", count: 5,
    },
    {
      id: "obj_fragments", kind: "collect",
      label: { fr: "Rassemble les 6 fragments du Psaume 119:105 sur la Lampe de la Parole", en: "Gather the 6 fragments of Psalm 119:105 onto the Lamp of the Word" },
      target: "lampe_parole", count: 6,
    },
    {
      id: "obj_bapteme", kind: "reach",
      label: { fr: "Tiens-toi dans la rivière du baptême", en: "Stand in the river of baptism" },
      target: "riviere_bapteme",
    },
    {
      id: "obj_source", kind: "reach",
      label: { fr: "Puise à la Source d'eau vive près du puits", en: "Draw from the Spring of living water by the well" },
      target: "source_eau_vive",
    },
    {
      id: "obj_jardin", kind: "build",
      label: { fr: "Plante les 6 semences dans le jardin du cœur", en: "Plant the 6 seeds in the garden of the heart" },
      target: "jardin_coeur", count: 6,
    },
    {
      id: "obj_autel", kind: "build",
      label: { fr: "Assemble les 5 pierres vivantes sur l'autel de prière", en: "Set the 5 living stones on the prayer altar" },
      target: "autel_pierres", count: 5,
    },
    {
      id: "obj_table", kind: "build",
      label: { fr: "Apporte les 5 provisions à la table du partage", en: "Bring the 5 provisions to the table of sharing" },
      target: "table_partage", count: 5,
    },
    {
      id: "obj_brebis", kind: "guide",
      label: { fr: "Ramène les 3 brebis égarées à la bergerie du Bon Berger", en: "Lead the 3 lost sheep back to the Good Shepherd's fold" },
      target: "bergerie_bonne", count: 3,
    },
    {
      id: "obj_nicodeme", kind: "reach",
      label: { fr: "Écoute Nicodème sur la voie étroite", en: "Listen to Nicodemus on the narrow way" },
      target: "nicodeme",
    },
    {
      id: "obj_cloche", kind: "reach",
      label: { fr: "Fais sonner la Cloche de la Mission avant la porte", en: "Sound the Bell of the Mission before the gate" },
      target: "cloche_mission",
    },
    {
      id: "obj_porte", kind: "reach",
      label: { fr: "Franchis la Porte Étroite lumineuse", en: "Pass through the luminous Narrow Gate" },
      target: "narrow_gate",
    },
  ],
  panels: [
    {
      id: "p_mt7",
      title: { fr: "La porte étroite", en: "The narrow gate" },
      reference: "Matthieu 7:13-14",
      passage: {
        fr: "Entrez par la porte étroite. Car large est la porte, spacieux est le chemin qui mènent à la perdition, et il y en a beaucoup qui entrent par là. Mais étroite est la porte, resserré le chemin qui mènent à la vie, et il y en a peu qui les trouvent.",
        en: "Enter in by the narrow gate; for wide is the gate and broad is the way that leads to destruction, and many are those who enter in by it. How narrow is the gate, and restricted is the way that leads to life! Few are those who find it.",
      },
      explain: {
        fr: "Jésus décrit deux chemins : le large, facile et fréquenté, mène à la perdition ; l'étroit, exigeant mais vivifiant, mène à la vie. Suivre Christ commence par le choix délibéré de franchir la porte étroite. Peu la trouvent, non qu'elle soit cachée, mais parce qu'elle demande de tout remettre à Dieu.",
        en: "Jesus describes two ways: the broad one, easy and crowded, leads to destruction; the narrow one, demanding yet life-giving, leads to life. Following Christ begins with the deliberate choice to enter the narrow gate. Few find it, not because it is hidden, but because it asks us to surrender everything to God.",
      },
      didYouKnow: {
        fr: "Dans les villes antiques, à côté de la grande porte des caravanes existait souvent une petite porte basse : on ne pouvait y passer qu'à pied, sans charge — image du disciple qui laisse ses fardeaux derrière lui (Hébreux 12:1).",
        en: "In ancient cities, beside the great caravan gate there was often a small low door: one could only pass through on foot, unburdened — a picture of the disciple who leaves his loads behind (Hebrews 12:1).",
      },
      application: {
        fr: "Quel fardeau Dieu t'invite-t-il à déposer aujourd'hui pour avancer plus léger sur son chemin ?",
        en: "What burden is God inviting you to lay down today so you can walk His way unburdened?",
      },
    },
    {
      id: "p_jn3",
      title: { fr: "Naître de nouveau", en: "Born anew" },
      reference: "Jean 3:3",
      passage: {
        fr: "Jésus lui répondit: En vérité, en vérité, je te le dis, si un homme ne naît de nouveau, il ne peut voir le royaume de Dieu.",
        en: "Jesus answered him, \"Most certainly, I tell you, unless one is born anew, he can't see God's Kingdom.\"",
      },
      explain: {
        fr: "Nicodème, un chef religieux, vient trouver Jésus de nuit avec ses questions. Jésus ne lui demande pas plus d'efforts, mais une vie entièrement nouvelle, donnée par l'Esprit. Le parcours du disciple ne commence pas par ce que nous faisons, mais par ce que Dieu fait naître en nous.",
        en: "Nicodemus, a religious leader, comes to Jesus by night with his questions. Jesus does not ask him for more effort, but for an entirely new life, given by the Spirit. The disciple's journey does not begin with what we do, but with what God brings to birth in us.",
      },
      didYouKnow: {
        fr: "Le mot grec « anôthen » signifie à la fois « de nouveau » et « d'en haut » : la nouvelle naissance vient d'en haut, comme un don.",
        en: "The Greek word \"anothen\" means both \"anew\" and \"from above\": the new birth comes from above, as a gift.",
      },
      application: {
        fr: "Comme Nicodème, viens avec tes vraies questions : Jésus accueille celui qui cherche sincèrement.",
        en: "Like Nicodemus, come with your real questions: Jesus welcomes the one who sincerely seeks.",
      },
    },
    {
      id: "p_mt13",
      title: { fr: "Les épines qui étouffent", en: "The thorns that choke" },
      reference: "Matthieu 13:22",
      passage: {
        fr: "Celui qui a reçu la semence parmi les épines, c'est celui qui entend la parole, mais en qui les soucis du siècle et la séduction des richesses étouffent cette parole, et la rendent infructueuse.",
        en: "What was sown among the thorns, this is he who hears the word, but the cares of this age and the deceitfulness of riches choke the word, and he becomes unfruitful.",
      },
      explain: {
        fr: "Les stèles sombres de la large avenue brillent de loin, mais elles n'ont rien à donner. Jésus avertit que les soucis et l'attrait des richesses peuvent étouffer la Parole comme des épines étouffent une jeune pousse. Garder son cœur comme un jardin, c'est choisir chaque jour ce qu'on y laisse pousser.",
        en: "The dark steles of the broad avenue glitter from afar, but they have nothing to give. Jesus warns that worries and the lure of riches can choke the Word like thorns choke a young shoot. Keeping your heart like a garden means choosing every day what you let grow there.",
      },
      didYouKnow: {
        fr: "Dans la même parabole, la bonne terre porte du fruit « cent, soixante, ou trente pour un » (Matthieu 13:23) : l'espérance du jardin du cœur.",
        en: "In the same parable, the good soil bears fruit \"a hundred, sixty, or thirty times\" (Matthew 13:23): the hope of the garden of the heart.",
      },
      application: {
        fr: "Quelle « épine » (souci, écran, envie) occupe aujourd'hui la place que la Parole devrait tenir dans ton cœur ?",
        en: "Which \"thorn\" (worry, screen, craving) is taking the place the Word should hold in your heart today?",
      },
    },
    {
      id: "p_ps119",
      title: { fr: "La lampe de la Parole", en: "The lamp of the Word" },
      reference: "Psaume 119:105",
      passage: {
        fr: "Ta parole est une lampe à mes pieds, Et une lumière sur mon sentier.",
        en: "Your word is a lamp to my feet, and a light for my path.",
      },
      explain: {
        fr: "Sur le sentier resserré, on n'avance pas à la lumière des lampions de la foire, mais à celle de la Parole. Une lampe à mes pieds n'éclaire pas tout l'horizon : elle éclaire le pas suivant. Rassembler les fragments du verset, c'est apprendre la Parole par cœur pour qu'elle éclaire chaque décision du jour.",
        en: "On the narrow path one does not walk by the fair's lanterns, but by the light of the Word. A lamp to my feet does not light the whole horizon: it lights the next step. Gathering the fragments of the verse is learning the Word by heart so it can light each decision of the day.",
      },
      didYouKnow: {
        fr: "Le Psaume 119 est le plus long chapitre de la Bible : 176 versets, presque tous consacrés à la Parole de Dieu.",
        en: "Psalm 119 is the longest chapter in the Bible: 176 verses, almost all of them about the Word of God.",
      },
      application: {
        fr: "Quel verset pourrais-tu apprendre par cœur cette semaine pour qu'il éclaire ton prochain pas ?",
        en: "Which verse could you memorize this week so it lights your next step?",
      },
    },
    {
      id: "p_ac2",
      title: { fr: "Repentance, baptême et communauté", en: "Repentance, baptism and community" },
      reference: "Actes 2:38",
      passage: {
        fr: "Pierre leur dit: Repentez-vous, et que chacun de vous soit baptisé au nom de Jésus-Christ, pour le pardon de vos péchés; et vous recevrez le don du Saint-Esprit.",
        en: "Peter said to them, \"Repent, and be baptized, every one of you, in the name of Jesus Christ for the forgiveness of sins, and you will receive the gift of the Holy Spirit.\"",
      },
      explain: {
        fr: "Le jour de la Pentecôte, Pierre montre le chemin d'entrée : se repentir, être baptisé, recevoir l'Esprit. Puis les nouveaux disciples persévèrent ensemble : enseignement, communion fraternelle, fraction du pain et prières (Actes 2:42). Déposer ses fardeaux dans l'eau et dresser la table du partage racontent ce même mouvement : on naît de nouveau POUR une famille.",
        en: "On the day of Pentecost, Peter shows the way in: repent, be baptized, receive the Spirit. Then the new disciples persevere together: teaching, fellowship, breaking of bread and prayers (Acts 2:42). Laying burdens into the water and setting the table of sharing tell that same movement: we are born anew INTO a family.",
      },
      didYouKnow: {
        fr: "Actes 2:44-46 raconte que les premiers disciples « avaient tout en commun » et rompaient le pain « avec joie et simplicité de cœur » — la table du partage vient de là.",
        en: "Acts 2:44-46 tells that the first disciples \"had all things in common\" and broke bread \"with gladness and singleness of heart\" — the table of sharing comes from there.",
      },
      application: {
        fr: "Avec qui pourrais-tu partager un repas ou une prière cette semaine, comme la première communauté ?",
        en: "With whom could you share a meal or a prayer this week, like the first community?",
      },
    },
    {
      id: "p_mt28",
      title: { fr: "Faites des disciples", en: "Make disciples" },
      reference: "Matthieu 28:19-20",
      passage: {
        fr: "Allez, faites de toutes les nations des disciples, les baptisant au nom du Père, du Fils et du Saint-Esprit, et enseignez-leur à observer tout ce que je vous ai prescrit. Et voici, je suis avec vous tous les jours, jusqu'à la fin du monde.",
        en: "Go and make disciples of all nations, baptizing them in the name of the Father and of the Son and of the Holy Spirit, teaching them to observe all things that I commanded you. Behold, I am with you always, even to the end of the age.",
      },
      explain: {
        fr: "Le parcours ne s'arrête pas à la porte : celui qui est devenu disciple est envoyé pour en faire d'autres. André amène son frère Simon, Philippe explique l'Écriture à l'Éthiopien, la Samaritaine raconte sa rencontre à toute sa ville. La cloche de la mission sonne pour toi aussi — et la promesse tient : « je suis avec vous tous les jours ».",
        en: "The journey does not stop at the gate: the one who has become a disciple is sent to make others. Andrew brings his brother Simon, Philip explains the Scripture to the Ethiopian, the Samaritan woman tells her whole town about her encounter. The bell of the mission rings for you too — and the promise holds: \"I am with you always.\"",
      },
      didYouKnow: {
        fr: "Ces versets sont souvent appelés « le Grand Mandat » : ce sont parmi les dernières paroles de Jésus rapportées par Matthieu avant son ascension.",
        en: "These verses are often called \"the Great Commission\": they are among the last words of Jesus recorded by Matthew before his ascension.",
      },
      application: {
        fr: "Comme André, qui pourrais-tu simplement inviter à venir voir Jésus ?",
        en: "Like Andrew, whom could you simply invite to come and see Jesus?",
      },
    },
  ],
  quizzes: [
    {
      id: "q_narrow_gate", level: "D",
      prompt: { fr: "D'après Jésus, par quelle porte entre-t-on dans la vie ?", en: "According to Jesus, through which gate does one enter life?" },
      choices: [
        { fr: "La porte étroite", en: "The narrow gate" },
        { fr: "La porte large", en: "The wide gate" },
        { fr: "N'importe quelle porte", en: "Any gate" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Entrez par la porte étroite » (Matthieu 7:13-14). Le chemin de la vie se choisit et se suit avec persévérance.",
        en: "\"Enter in by the narrow gate\" (Matthew 7:13-14). The way to life is chosen and followed with perseverance.",
      },
    },
    {
      id: "q_lampe", level: "D",
      prompt: { fr: "D'après le Psaume 119:105, qu'est-ce que la Parole de Dieu pour nos pas ?", en: "According to Psalm 119:105, what is God's Word for our steps?" },
      choices: [
        { fr: "Une lampe et une lumière", en: "A lamp and a light" },
        { fr: "Une carte au trésor", en: "A treasure map" },
        { fr: "Un fardeau à porter", en: "A burden to carry" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Ta parole est une lampe à mes pieds, et une lumière sur mon sentier » (Psaume 119:105). Elle éclaire le pas suivant sur la voie étroite.",
        en: "\"Your word is a lamp to my feet, and a light for my path\" (Psalm 119:105). It lights the next step on the narrow way.",
      },
    },
    {
      id: "q_nicodeme", level: "A",
      prompt: { fr: "Que dit Jésus à Nicodème sur ce qu'il faut pour voir le royaume de Dieu (Jean 3:3) ?", en: "What does Jesus tell Nicodemus is needed to see God's Kingdom (John 3:3)?" },
      choices: [
        { fr: "Observer plus de règles religieuses", en: "Keep more religious rules" },
        { fr: "Naître de nouveau", en: "Be born anew" },
        { fr: "Offrir un plus grand sacrifice au temple", en: "Offer a greater sacrifice at the temple" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Si un homme ne naît de nouveau, il ne peut voir le royaume de Dieu » (Jean 3:3). Ce n'est pas un effort de plus, mais une vie nouvelle donnée par l'Esprit.",
        en: "\"Unless one is born anew, he can't see God's Kingdom\" (John 3:3). It is not one more effort, but a new life given by the Spirit.",
      },
    },
    {
      id: "q_pierres_vivantes", level: "A",
      prompt: { fr: "Selon 1 Pierre 2:5, à quoi les croyants sont-ils comparés pour bâtir une maison spirituelle ?", en: "According to 1 Peter 2:5, what are believers compared to for building a spiritual house?" },
      choices: [
        { fr: "Des colonnes de marbre", en: "Marble pillars" },
        { fr: "Des grains de sable", en: "Grains of sand" },
        { fr: "Des pierres vivantes", en: "Living stones" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Vous-mêmes, comme des pierres vivantes, édifiez-vous pour former une maison spirituelle » (1 Pierre 2:5). Chaque disciple a sa place dans l'édifice que Dieu construit.",
        en: "\"You also as living stones are built up as a spiritual house\" (1 Peter 2:5). Every disciple has a place in the house God is building.",
      },
    },
    {
      id: "q_epines", level: "E",
      prompt: { fr: "Dans la parabole du semeur, qu'est-ce qui étouffe la Parole chez celui qui a reçu la semence parmi les épines (Matthieu 13:22) ?", en: "In the parable of the sower, what chokes the Word in the one who received the seed among thorns (Matthew 13:22)?" },
      choices: [
        { fr: "La persécution à cause de la Parole", en: "Persecution because of the Word" },
        { fr: "Le manque d'intelligence", en: "A lack of understanding" },
        { fr: "Les soucis du siècle et la séduction des richesses", en: "The cares of this age and the deceitfulness of riches" },
      ],
      answerIndex: 2,
      explain: {
        fr: "La persécution fait tomber le sol pierreux (v.21) et le manque d'intelligence concerne le bord du chemin (v.19) ; parmi les épines, ce sont « les soucis du siècle et la séduction des richesses » qui étouffent la Parole (Matthieu 13:22). Discerner SON épine, c'est déjà commencer à la déraciner.",
        en: "Persecution topples the rocky ground (v.21) and lack of understanding belongs to the path (v.19); among the thorns it is \"the cares of this age and the deceitfulness of riches\" that choke the Word (Matthew 13:22). Naming YOUR thorn is the first step to uprooting it.",
      },
    },
    {
      id: "qD3", level: "D",
      prompt: { fr: "Selon Matthieu 7:13, où mène le chemin large et spacieux ?", en: "According to Matthew 7:13, where does the wide and broad way lead?" },
      choices: [
        { fr: "À un raccourci vers la porte étroite", en: "To a shortcut to the narrow gate" },
        { fr: "À la perdition", en: "To destruction" },
        { fr: "À la bergerie du Bon Berger", en: "To the Good Shepherd's fold" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Large est la porte, spacieux est le chemin qui mènent à la perdition, et il y en a beaucoup qui entrent par là » (Matthieu 7:13). Le grand nombre ne rend pas un chemin sûr.",
        en: "\"Wide is the gate and broad is the way that leads to destruction, and many are those who enter in by it\" (Matthew 7:13). A crowd does not make a road safe.",
      },
    },
    {
      id: "qD4", level: "D",
      prompt: { fr: "Au puits, que promet Jésus à la Samaritaine (Jean 4:14) ?", en: "At the well, what does Jesus promise the Samaritan woman (John 4:14)?" },
      choices: [
        { fr: "Une eau qui deviendra en elle une source jaillissant jusque dans la vie éternelle", en: "Water that will become in her a spring welling up to eternal life" },
        { fr: "Un puits plus profond que celui de Jacob", en: "A well deeper than Jacob's" },
        { fr: "Une cruche neuve pour puiser", en: "A new jar to draw with" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« L'eau que je lui donnerai deviendra en lui une source d'eau qui jaillira jusque dans la vie éternelle » (Jean 4:14). Jésus offre une vie que rien n'assèche.",
        en: "\"The water that I will give him will become in him a well of water springing up to eternal life\" (John 4:14). Jesus offers a life nothing can dry up.",
      },
    },
    {
      id: "qD5", level: "D",
      prompt: { fr: "Dans Luc 15:4, que fait le berger quand il a perdu une brebis sur cent ?", en: "In Luke 15:4, what does the shepherd do when one sheep out of a hundred is lost?" },
      choices: [
        { fr: "Il attend qu'elle revienne d'elle-même", en: "He waits for it to come back on its own" },
        { fr: "Il en achète une nouvelle au marché", en: "He buys a new one at the market" },
        { fr: "Il laisse les quatre-vingt-dix-neuf et va chercher celle qui est perdue", en: "He leaves the ninety-nine and goes after the one that is lost" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Quel homme d'entre vous, s'il a cent brebis, et qu'il en perde une, ne laisse les quatre-vingt-dix-neuf autres… pour aller après celle qui est perdue, jusqu'à ce qu'il la retrouve ? » (Luc 15:4). Chaque brebis compte pour le berger.",
        en: "\"Which of you men, if you had one hundred sheep and lost one of them, wouldn't leave the ninety-nine… and go after the one that was lost, until he found it?\" (Luke 15:4). Every sheep matters to the shepherd.",
      },
    },
    {
      id: "qA3", level: "A",
      prompt: { fr: "Après avoir rencontré Jésus, qui André amène-t-il à lui (Jean 1:41-42) ?", en: "After meeting Jesus, whom does Andrew bring to him (John 1:41-42)?" },
      choices: [
        { fr: "Son frère Simon", en: "His brother Simon" },
        { fr: "Philippe l'évangéliste", en: "Philip the evangelist" },
        { fr: "Nicodème", en: "Nicodemus" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Il trouva d'abord son propre frère Simon… et il le conduisit vers Jésus » (Jean 1:41-42). Le premier champ de mission d'André, c'est sa propre famille.",
        en: "\"He first found his own brother, Simon… and he brought him to Jesus\" (John 1:41-42). Andrew's first mission field is his own family.",
      },
    },
    {
      id: "qA4", level: "A",
      prompt: { fr: "D'après Galates 5:22, l'amour, la joie, la paix et la patience sont…", en: "According to Galatians 5:22, love, joy, peace and patience are…" },
      choices: [
        { fr: "Des dons réservés aux apôtres", en: "Gifts reserved for the apostles" },
        { fr: "Le fruit de l'Esprit", en: "The fruit of the Spirit" },
        { fr: "Des récompenses pour les plus zélés", en: "Rewards for the most zealous" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Mais le fruit de l'Esprit, c'est l'amour, la joie, la paix, la patience… » (Galates 5:22). Ce fruit pousse dans le cœur où l'Esprit a été semé — comme les semences du jardin de cette scène.",
        en: "\"But the fruit of the Spirit is love, joy, peace, patience…\" (Galatians 5:22). That fruit grows in the heart where the Spirit has been sown — like the seeds in this scene's garden.",
      },
    },
    {
      id: "qA5", level: "A",
      prompt: { fr: "Selon Actes 2:38, quel est le bon ordre du chemin annoncé par Pierre ?", en: "According to Acts 2:38, what is the right order of the way Peter announces?" },
      choices: [
        { fr: "Recevoir l'Esprit, puis se repentir, puis être baptisé", en: "Receive the Spirit, then repent, then be baptized" },
        { fr: "Être baptisé, puis se repentir, puis recevoir l'Esprit", en: "Be baptized, then repent, then receive the Spirit" },
        { fr: "Se repentir, être baptisé, recevoir le don du Saint-Esprit", en: "Repent, be baptized, receive the gift of the Holy Spirit" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Repentez-vous, et que chacun de vous soit baptisé… et vous recevrez le don du Saint-Esprit » (Actes 2:38). La repentance ouvre le chemin, le baptême le marque, l'Esprit est donné.",
        en: "\"Repent, and be baptized, every one of you… and you will receive the gift of the Holy Spirit\" (Acts 2:38). Repentance opens the way, baptism marks it, the Spirit is given.",
      },
    },
    {
      id: "qE2", level: "E",
      prompt: { fr: "Comment Matthieu 7:14 décrit-il le chemin qui mène à la vie ?", en: "How does Matthew 7:14 describe the way that leads to life?" },
      choices: [
        { fr: "Étroite est la porte, resserré le chemin, et il y en a peu qui les trouvent", en: "Narrow is the gate, restricted the way, and few are those who find them" },
        { fr: "Court est le chemin, et tous le connaissent", en: "Short is the way, and everyone knows it" },
        { fr: "Caché est le chemin, et nul ne peut le trouver", en: "Hidden is the way, and no one can find it" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Étroite est la porte, resserré le chemin qui mènent à la vie, et il y en a peu qui les trouvent » (Matthieu 7:14). Le chemin n'est pas caché : il demande un choix que peu consentent à faire.",
        en: "\"How narrow is the gate, and restricted is the way that leads to life! Few are those who find it\" (Matthew 7:14). The way is not hidden: it asks a choice few consent to make.",
      },
    },
    {
      id: "qE3", level: "E",
      prompt: { fr: "Quelle particularité le Psaume 119, d'où vient le verset de la Lampe, a-t-il dans la Bible ?", en: "What distinction does Psalm 119, source of the Lamp verse, hold in the Bible?" },
      choices: [
        { fr: "C'est le premier psaume du recueil", en: "It is the first psalm of the collection" },
        { fr: "C'est le plus long chapitre de la Bible : 176 versets, presque tous consacrés à la Parole", en: "It is the longest chapter in the Bible: 176 verses, almost all about the Word" },
        { fr: "C'est un chant de victoire de David après Goliath", en: "It is David's victory song after Goliath" },
      ],
      answerIndex: 1,
      explain: {
        fr: "Le Psaume 119 compte 176 versets — le plus long chapitre de la Bible — presque tous consacrés à la Parole de Dieu, dont « Ta parole est une lampe à mes pieds » (Psaume 119:105).",
        en: "Psalm 119 has 176 verses — the longest chapter in the Bible — almost all devoted to God's Word, including \"Your word is a lamp to my feet\" (Psalm 119:105).",
      },
    },
    {
      id: "qE4", level: "E",
      prompt: { fr: "Selon Luc 15:7, que se passe-t-il dans le ciel quand un seul pécheur se repent ?", en: "According to Luke 15:7, what happens in heaven when a single sinner repents?" },
      choices: [
        { fr: "Un silence solennel", en: "A solemn silence" },
        { fr: "Il y a plus de joie que pour quatre-vingt-dix-neuf justes qui n'ont pas besoin de repentance", en: "There is more joy than over ninety-nine righteous who need no repentance" },
        { fr: "Une simple mention dans un registre", en: "A mere mention in a register" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Il y aura plus de joie dans le ciel pour un seul pécheur qui se repent, que pour quatre-vingt-dix-neuf justes qui n'ont pas besoin de repentance » (Luc 15:7). Ramener une brebis, c'est la fête du ciel.",
        en: "\"There will be more joy in heaven over one sinner who repents, than over ninety-nine righteous people who need no repentance\" (Luke 15:7). Bringing one sheep home is heaven's feast.",
      },
    },
    {
      id: "qD6", level: "D",
      prompt: { fr: "Près de quelle ville de Samarie Jésus rencontre-t-il la Samaritaine (Jean 4:5-6) ?", en: "Near which town of Samaria does Jesus meet the Samaritan woman (John 4:5-6)?" },
      choices: [
        { fr: "Sychar, près du puits de Jacob", en: "Sychar, near Jacob's well" },
        { fr: "Capernaüm, au bord du lac", en: "Capernaum, by the lake" },
        { fr: "Jéricho, près du Jourdain", en: "Jericho, near the Jordan" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Il arriva dans une ville de Samarie, nommée Sychar… Là se trouvait le puits de Jacob » (Jean 4:5-6). Fatigué du voyage, Jésus s'assied au bord du puits — et c'est là que jaillit la promesse de l'eau vive.",
        en: "\"He came to a city of Samaria called Sychar… Jacob's well was there\" (John 4:5-6). Tired from the journey, Jesus sits by the well — and there the promise of living water springs up.",
      },
    },
    {
      id: "qD7", level: "D",
      prompt: { fr: "Qu'annonce André à son frère Simon après avoir passé un moment avec Jésus (Jean 1:41) ?", en: "What does Andrew announce to his brother Simon after spending time with Jesus (John 1:41)?" },
      choices: [
        { fr: "« Nous avons trouvé un nouveau maître de la Loi »", en: "\"We have found a new teacher of the Law\"" },
        { fr: "« Nous avons trouvé le chemin de Jérusalem »", en: "\"We have found the road to Jerusalem\"" },
        { fr: "« Nous avons trouvé le Messie »", en: "\"We have found the Messiah\"" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Il trouva d'abord son propre frère Simon, et il lui dit: Nous avons trouvé le Messie (ce qui signifie Christ) » (Jean 1:41). Le premier témoignage tient en une phrase simple : dire qui l'on a trouvé.",
        en: "\"He first found his own brother, Simon, and said to him, 'We have found the Messiah!' (which is, being interpreted, Christ)\" (John 1:41). The first testimony fits in one simple sentence: saying whom you have found.",
      },
    },
    {
      id: "qD8", level: "D",
      prompt: { fr: "Dans la parabole du semeur, que fait la semence tombée dans la bonne terre (Matthieu 13:23) ?", en: "In the parable of the sower, what does the seed in the good soil do (Matthew 13:23)?" },
      choices: [
        { fr: "Elle reste enfouie sans changer", en: "It stays buried unchanged" },
        { fr: "Elle porte du fruit : cent, soixante ou trente pour un", en: "It bears fruit: a hundred, sixty, or thirty times" },
        { fr: "Elle pousse puis sèche au soleil", en: "It sprouts then withers in the sun" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Celui qui a reçu la semence dans la bonne terre… porte du fruit, et un grain en donne cent, un autre soixante, un autre trente » (Matthieu 13:23). Le cœur qui écoute et comprend la Parole devient un jardin qui déborde.",
        en: "\"What was sown on the good ground… bears fruit, some one hundred times as much, some sixty, and some thirty\" (Matthew 13:23). The heart that hears and understands the Word becomes an overflowing garden.",
      },
    },
    {
      id: "qD9", level: "D",
      prompt: { fr: "Quelle promesse accompagne l'ordre de faire des disciples (Matthieu 28:20) ?", en: "What promise accompanies the command to make disciples (Matthew 28:20)?" },
      choices: [
        { fr: "« Je suis avec vous tous les jours, jusqu'à la fin du monde »", en: "\"I am with you always, even to the end of the age\"" },
        { fr: "« Vous ne rencontrerez aucune difficulté en chemin »", en: "\"You will meet no difficulty on the way\"" },
        { fr: "« Vous serez admirés de tous les peuples »", en: "\"You will be admired by all the nations\"" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Et voici, je suis avec vous tous les jours, jusqu'à la fin du monde » (Matthieu 28:20). L'envoyé ne part jamais seul : la présence de Jésus est la provision de la mission.",
        en: "\"Behold, I am with you always, even to the end of the age\" (Matthew 28:20). The one who is sent never goes alone: Jesus' presence is the mission's provision.",
      },
    },
    {
      id: "qA6", level: "A",
      prompt: { fr: "Quel livre l'Éthiopien lisait-il sur son char quand Philippe le rejoignit (Actes 8:28-30) ?", en: "Which book was the Ethiopian reading in his chariot when Philip joined him (Acts 8:28-30)?" },
      choices: [
        { fr: "Le livre des Psaumes", en: "The book of Psalms" },
        { fr: "Le livre de la Genèse", en: "The book of Genesis" },
        { fr: "Le prophète Ésaïe", en: "The prophet Isaiah" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Assis sur son char, il lisait le prophète Ésaïe » (Actes 8:28). C'est à partir de ce passage qu'ensuite Philippe « lui annonça la bonne nouvelle de Jésus » (Actes 8:35) : l'Écriture est le point de départ de la rencontre.",
        en: "\"He was… sitting in his chariot, and was reading the prophet Isaiah\" (Acts 8:28). Beginning from that Scripture, Philip then \"preached to him Jesus\" (Acts 8:35): the Word is the meeting's starting point.",
      },
    },
    {
      id: "qA7", level: "A",
      prompt: { fr: "Que fait la Samaritaine aussitôt après sa rencontre avec Jésus (Jean 4:28-29) ?", en: "What does the Samaritan woman do right after meeting Jesus (John 4:28-29)?" },
      choices: [
        { fr: "Elle remplit sa cruche et rentre en silence", en: "She fills her jar and goes home in silence" },
        { fr: "Elle laisse sa cruche et court dire à la ville : « Venez voir »", en: "She leaves her jar and runs to tell the town: \"Come, see\"" },
        { fr: "Elle demande à Jésus de garder le secret", en: "She asks Jesus to keep it secret" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Alors la femme, ayant laissé sa cruche, s'en alla dans la ville, et dit aux gens: Venez voir un homme qui m'a dit tout ce que j'ai fait » (Jean 4:28-29). Beaucoup de Samaritains crurent à cause de sa parole (Jean 4:39) : la cruche laissée dit l'urgence joyeuse du témoignage.",
        en: "\"So the woman left her water pot, went away into the city, and said to the people, 'Come, see a man who told me everything that I did'\" (John 4:28-29). Many Samaritans believed because of her word (John 4:39): the jar left behind tells the joyful urgency of witness.",
      },
    },
    {
      id: "qA8", level: "A",
      prompt: { fr: "Avant de parler des croyants, qui 1 Pierre 2:4 appelle-t-il la pierre vivante, rejetée par les hommes mais choisie et précieuse devant Dieu ?", en: "Before speaking of believers, whom does 1 Peter 2:4 call the living stone, rejected by men but chosen by God and precious?" },
      choices: [
        { fr: "Le Seigneur Jésus, dont on s'approche", en: "The Lord Jesus, to whom we come" },
        { fr: "L'apôtre Pierre lui-même", en: "The apostle Peter himself" },
        { fr: "Le temple de Jérusalem", en: "The temple in Jerusalem" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Approchez-vous de lui, pierre vivante, rejetée par les hommes, mais choisie et précieuse devant Dieu » (1 Pierre 2:4). Les pierres vivantes de l'autel tiennent parce qu'elles s'appuient d'abord sur LA pierre vivante : Christ.",
        en: "\"Come to him, a living stone, rejected indeed by men, but chosen by God, precious\" (1 Peter 2:4). The altar's living stones hold because they rest first on THE living stone: Christ.",
      },
    },
    {
      id: "qE5", level: "E",
      prompt: { fr: "Selon Actes 1:8, dans quel ordre les disciples seront-ils témoins de Jésus ?", en: "According to Acts 1:8, in what order will the disciples be Jesus' witnesses?" },
      choices: [
        { fr: "Rome, puis Athènes, puis Jérusalem", en: "Rome, then Athens, then Jerusalem" },
        { fr: "Jérusalem, la Judée, la Samarie, et jusqu'aux extrémités de la terre", en: "Jerusalem, Judea, Samaria, and to the uttermost parts of the earth" },
        { fr: "D'abord les extrémités de la terre, puis Jérusalem", en: "First the ends of the earth, then Jerusalem" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Vous serez mes témoins à Jérusalem, dans toute la Judée, dans la Samarie, et jusqu'aux extrémités de la terre » (Actes 1:8). La mission s'élargit en cercles : elle commence là où l'on est, comme André avec son propre frère.",
        en: "\"You will be witnesses to me in Jerusalem, in all Judea and Samaria, and to the uttermost parts of the earth\" (Acts 1:8). The mission widens in circles: it starts where you are, like Andrew with his own brother.",
      },
    },
    {
      id: "qE6", level: "E",
      prompt: { fr: "Que nous invite à rejeter Hébreux 12:1 pour courir la course qui nous est ouverte ?", en: "What does Hebrews 12:1 invite us to lay aside in order to run the race set before us?" },
      choices: [
        { fr: "Nos compagnons de route trop lents", en: "Our companions who walk too slowly" },
        { fr: "Les témoins qui nous entourent", en: "The witnesses who surround us" },
        { fr: "Tout fardeau et le péché qui nous enveloppe si facilement", en: "Every weight and the sin which so easily entangles us" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Rejetons tout fardeau, et le péché qui nous enveloppe si facilement, et courons avec persévérance dans la carrière qui nous est ouverte » (Hébreux 12:1). Les fardeaux déposés dans les eaux de la repentance racontent ce délestage : on court léger, entouré d'une nuée de témoins.",
        en: "\"Let us also lay aside every weight and the sin which so easily entangles us, and let us run with perseverance the race that is set before us\" (Hebrews 12:1). The burdens laid into the waters of repentance tell that unburdening: we run light, surrounded by a cloud of witnesses.",
      },
    },
  ],
  tips: [
    { fr: "Ramasse chaque fardeau nommé (orgueil, peur, amertume, doute, mensonge) et dépose-le dans les eaux de la repentance avant de gagner la porte.", en: "Pick up each named burden (pride, fear, bitterness, doubt, falsehood) and lay it into the waters of repentance before heading for the gate." },
    { fr: "Les stèles sombres et les lumières dorées de la foire brillent mais ne mènent nulle part — reste sur la voie étroite bordée d'arbres.", en: "The dark steles and golden fair lights glitter but lead nowhere — stay on the tree-lined narrow way." },
    { fr: "Les 6 fragments du Psaume 119:105 jalonnent la voie étroite : rassemble-les sur la Lampe de la Parole pour éclairer la montée.", en: "The 6 fragments of Psalm 119:105 line the narrow way: gather them onto the Lamp of the Word to light the climb." },
    { fr: "Tiens-toi quelques secondes dans la rivière du baptême : « Repentez-vous, et soyez baptisés » (Actes 2:38).", en: "Stand a few seconds in the river of baptism: \"Repent, and be baptized\" (Acts 2:38)." },
    { fr: "Plante les six semences (foi, amour, espérance, paix, joie, patience) dans le jardin du cœur, puis écoute Nicodème sur la voie étroite.", en: "Plant the six seeds (faith, love, hope, peace, joy, patience) in the garden of the heart, then listen to Nicodemus on the narrow way." },
    { fr: "Assemble les 5 pierres vivantes sur l'autel de prière (1 Pierre 2:5) et dresse la table du partage avec les 5 provisions (Actes 2:46).", en: "Set the 5 living stones on the prayer altar (1 Peter 2:5) and lay the table of sharing with the 5 provisions (Acts 2:46)." },
    { fr: "Trois brebis se sont égarées du côté de la foire : ramène-les une à une à la bergerie du Bon Berger (Luc 15:4-7).", en: "Three sheep have strayed toward the fair: lead them one by one back to the Good Shepherd's fold (Luke 15:4-7)." },
    { fr: "Avant de franchir la Porte Étroite, fais sonner la Cloche de la Mission : le disciple est envoyé pour faire des disciples (Matthieu 28:19).", en: "Before passing the Narrow Gate, sound the Bell of the Mission: the disciple is sent to make disciples (Matthew 28:19)." },
  ],
  lesson: {
    fr: "En vérité, en vérité, je te le dis, si un homme ne naît de nouveau, il ne peut voir le royaume de Dieu. (Jean 3:3)",
    en: "Most certainly I tell you, unless one is born anew, he can't see God's Kingdom. (John 3:3)",
  },
  audio: { ambience: "garden" },
  multiplayer: { mode: "coop", maxPlayers: 8 },
};

export default parcoursDuDisciple;
